import React from 'react';
import LazyLoad from 'react-lazyload';
import { arrayOf, shape, number, object, string } from 'prop-types';
import styles from './styles/styles.module.scss';
import { contextPropTypes } from '../../proptypes/shared';
import GenericGiftingBlock from '../blocks/GiftingBlock/GenericGiftingBlock';
import ShopifyPreview from '../blocks/ShopifyPreview';
import OnePercentBlock from '../blocks/OnePercentBlock';
import EditorsChoiceBlock from '../blocks/EditorsChoiceBlock';
import ExploreAndDiscover from '../blocks/ExploreAndDiscover';
import CommunityStats from '../blocks/CommunityStats/CommunityStats';
import TrailRiver from '../blocks/TrailRiver';
import SignupActivitySuitability from '../blocks/SignupActivitySuitability';
import useListItems from '../../hooks/useListItems';
import useSections from '../../hooks/useSections';
import ProUpsell from '../blocks/ProUpsell';
import ProUpsellNoAuth from '../blocks/ProUpsellNoAuth';
import ActivitiesRiver from '../blocks/ActivitiesRiver';
import SeoTrailTabbed from '../blocks/SeoTrailTabbed';
import { LanguageSupportUtil } from '../../utils/language_support_util';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import {
  ATTRIBUTE,
  COMMUNITY_STATS,
  EDITORS_CHOICE_APP,
  EXPLORE_AND_DISCOVER,
  GIFTING_UPSELL,
  MERCHANDISE_STORE,
  ON_TRACK_WITH_PRO_CHECKLIST,
  ON_TRACK_WITH_PRO,
  ONE_PERCENT_PLANET,
  SEO_TRAILS,
  SIGNUP_ACTIVITY_SUITABILITY,
  TRAIL_LIST
} from './constants';
import { UNITED_STATES } from '../../utils/constants/country_iso_codes';
import { ENGLISH } from '../../utils/constants/language_region_codes';

const {
  strings: {
    HOME_PAGE: { LOCAL_FAVORITES_NEAR }
  }
} = StringConstantsMixin;

const SectionSwitch = ({ context, initialSections, windowWidth, lists, locationData }) => {
  const { handleFavoriteClick, listItems, renderModal } = useListItems(lists, context);
  const sections = useSections(initialSections, locationData.latLng, context, listItems);

  const trailClass = idx => {
    // we need to update spacing here if there are two trails that are sequential.
    const prevSection = sections[idx - 1];
    let classes = '';
    if (context.currentUser && idx === 0) {
      classes += `${styles.authenticatedSpacing} `;
    }

    if (prevSection && prevSection.section_type === TRAIL_LIST) {
      classes += styles.secondTrailPadding;
    } else {
      classes += styles.trailRiverPadding;
    }

    return classes;
  };

  const trailRiverTitle = (section, idx) => {
    if (idx === 0 && locationData.city) {
      const shortRegionCode = context.languageRegionCode.split('-')[0];

      return (
        <>
          {`${LOCAL_FAVORITES_NEAR} `}
          <a className="xlate-none" href={LanguageSupportUtil.wrapUrlSafe('/explore?ref=homepage_explore_trailriver', context.languageRegionCode)}>
            {locationData.city.names[shortRegionCode]}
          </a>
        </>
      );
    }

    return section.title;
  };

  return (
    <>
      {renderModal()}
      {sections.map((section, idx) => {
        switch (section.section_type) {
          case ATTRIBUTE:
            return (
              <ActivitiesRiver
                title={section.title}
                activities={section.objects}
                windowWidth={windowWidth}
                languageRegionCode={context.languageRegionCode}
              />
            );
          case COMMUNITY_STATS:
            return <CommunityStats displayMetric={context.displayMetric} key={section.section_type} />;
          case ON_TRACK_WITH_PRO_CHECKLIST:
            return <ProUpsell languageRegionCode={context.languageRegionCode} windowWidth={windowWidth} />;
          case ON_TRACK_WITH_PRO:
            return <ProUpsellNoAuth key={section.section_type} languageRegionCode={context.languageRegionCode} windowWidth={windowWidth} />;
          case GIFTING_UPSELL:
            return <GenericGiftingBlock languageRegionCode={context.languageRegionCode} key={section.section_type} />;
          case MERCHANDISE_STORE:
            return (
              locationData.country === UNITED_STATES &&
              context.languageRegionCode === ENGLISH && (
                <LazyLoad>
                  <ShopifyPreview shopifyProducts={section.products} windowWidth={windowWidth} key={section.section_type} page="homepage" />
                </LazyLoad>
              )
            );
          case ONE_PERCENT_PLANET:
            return <OnePercentBlock languageRegionCode={context.languageRegionCode} proUser={context.proUser} key={section.section_type} />;
          case EDITORS_CHOICE_APP:
            return <EditorsChoiceBlock windowWidth={windowWidth} languageRegionCode={context.languageRegionCode} key={section.section_type} />;
          case EXPLORE_AND_DISCOVER:
            return <ExploreAndDiscover key={section.section_type} windowWidth={windowWidth} languageRegionCode={context.languageRegionCode} />;
          case SEO_TRAILS:
            return (
              <SeoTrailTabbed
                key={section.section_type}
                windowWidth={windowWidth}
                languageRegionCode={context.languageRegionCode}
                data={section.data}
                displayMetric={context.displayMetric}
              />
            );
          case SIGNUP_ACTIVITY_SUITABILITY:
            return (
              <SignupActivitySuitability
                activities={section.objects}
                key={section.section_type}
                mobileBrowser={context.mobileBrowser}
                windowWidth={windowWidth}
                languageRegionCode={context.languageRegionCode}
              />
            );
          case TRAIL_LIST:
            return (
              <TrailRiver
                {...section}
                context={context}
                windowWidth={windowWidth}
                handleFavoriteClick={handleFavoriteClick}
                listItems={listItems}
                className={trailClass(idx)}
                locationData={locationData}
                title={trailRiverTitle(section, idx)}
                trailCardLinkRef="homepage_trail_trailriver_trailresultcard"
                key={section.title}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

SectionSwitch.propTypes = {
  context: contextPropTypes.isRequired,
  initialSections: arrayOf(shape({ section_type: string })).isRequired,
  windowWidth: number,
  lists: shape({
    listItems: object,
    lists: object
  }).isRequired
};

SectionSwitch.defaultProps = {
  windowWidth: null
};

export default SectionSwitch;
